import type { Expression, Extension, PlanDefinition, PlanDefinitionAction } from "@medplum/fhirtypes";
import {
  asMemberId,
  asQuestionnaireScoreId,
  type FlowChoiceSetCondition,
  type FlowCondition,
  FlowDefinition,
  type FlowDefinitionProps,
  type FlowEdge,
  type FlowNode,
  type QuestionnaireCondition,
  type QuestionnaireConditionBehavior,
} from "@repo/domain";
import { extensionUrl } from "@repo/fhir-systems";
import { CATALOG_MODEL_KIND_SYSTEM, catalogModelKindTag, FLOW_DEFINITION_KIND } from "../../lib/catalog-kind";
import { catalogNameToFhir } from "../../lib/catalog-name";
import { PLAN_DEFINITION_KIND, planDefinitionKind } from "../../lib/plan-definition-kind";
import { omitUndefined } from "../omit-undefined";
import { CATALOG_AVAILABILITY } from "./catalog-availability";
import { type Codec, fp } from "./codec";
import { factConditionExtensions, withFactConditions } from "./patient-fact-condition";
import { required } from "./read";

// ============================================================
// FlowDefinition ⇄ PlanDefinition
//
// One top-level `action` per node, each a questionnaire carrying the form's
// lineage as a bare `definitionCanonical`. A node with no child actions is
// where a run ends; there is no end node.
//
// A node carries no layout: the builder lays the tree out every time it draws.
//
// An edge is a CHILD action of its source node, pointing at its target
// through `relatedAction` (this action happens before the target
// starts). Its conditions ride `action.condition` as one applicability
// expression. The expression is the domain's condition list as JSON under a
// private media type, not FHIRPath: the evaluator is the same one a form's
// enablement uses, and a FHIRPath rendering would be a second evaluator to
// keep in step with it.
// ============================================================

export const FLOW_NODE_SEQUENCE_URL = extensionUrl("flow-node-sequence");
export const FLOW_EDGE_SEQUENCE_URL = extensionUrl("flow-edge-sequence");
// Medplum types `Expression.language` as the three standard languages; the
// binding is extensible in FHIR, so a private media type is legal but has to
// be cast past the type.
export const FLOW_CONDITION_LANGUAGE = "application/x-mindforge-flow-conditions+json" as Expression["language"];

const OWNED_EXTENSION_URLS = new Set<string>([FLOW_NODE_SEQUENCE_URL, FLOW_EDGE_SEQUENCE_URL]);

type Props = FlowDefinitionProps;

interface StoredConditions {
  conditions: FlowCondition[];
  conditionBehavior?: QuestionnaireConditionBehavior;
}

function requiredOnAction<T>(field: string, value: T | undefined): T {
  if (value === undefined) throw new Error(`engine: flow PlanDefinition action is missing ${field}`);
  return value;
}

function extensionOf(resource: { extension?: Extension[] }, url: string): Extension | undefined {
  return resource.extension?.find((candidate) => candidate.url === url);
}

function nodeFromAction(action: PlanDefinitionAction): FlowNode {
  const id = asMemberId(requiredOnAction("id", action.id));
  const canonical = requiredOnAction("definitionCanonical", action.definitionCanonical);
  return { id, questionnaire: FlowDefinition.questionnaireRef(canonical) };
}

function edgeFromAction(from: FlowNode["id"], action: PlanDefinitionAction): FlowEdge {
  const target = action.relatedAction?.find((related) => related.relationship === "before-start")?.actionId;
  const expression = action.condition?.find(
    (condition) => condition.kind === "applicability" && condition.expression?.language === FLOW_CONDITION_LANGUAGE,
  )?.expression?.expression;
  const stored: StoredConditions = expression ? JSON.parse(expression) : { conditions: [] };
  return {
    id: asMemberId(requiredOnAction("id", action.id)),
    from,
    to: asMemberId(requiredOnAction("relatedAction.actionId", target)),
    conditions: withFactConditions(
      stored.conditions.map((condition): FlowCondition => {
        if ("fact" in condition) return condition;
        if ("score" in condition) {
          return {
            score: asQuestionnaireScoreId(condition.score),
            operator: condition.operator,
            value: condition.value,
          };
        }
        const question = asMemberId(condition.question);
        const { operator, value } = condition;
        if (value.kind === "Choices") {
          return {
            question,
            operator: operator as FlowChoiceSetCondition["operator"],
            value: { kind: "Choices", optionIds: value.optionIds.map(asMemberId) },
          };
        }
        return {
          question,
          operator: operator as QuestionnaireCondition["operator"],
          value: value.kind === "Choice" ? { kind: "Choice", optionId: asMemberId(value.optionId) } : value,
        };
      }),
      action.extension,
    ),
    ...(stored.conditionBehavior ? { conditionBehavior: stored.conditionBehavior } : {}),
  };
}

function graphFromActions(actions: readonly PlanDefinitionAction[] | undefined): Pick<Props, "nodes" | "edges"> {
  const nodes = (actions ?? []).map(nodeFromAction);
  const edges = (actions ?? []).flatMap((action, index) =>
    (action.action ?? []).map((child) => edgeFromAction((nodes[index] as FlowNode).id, child)),
  );
  return { nodes, edges };
}

function actionsFromGraph(props: Props): PlanDefinitionAction[] {
  return props.nodes.map((node) => {
    const routes = props.edges.filter((edge) => edge.from === node.id);
    // A leaf carries no `action` at all. The server drops an empty array on
    // write, so `[]` would never match what is stored, and every load of the
    // flow would read as an edit: the projection, which may only read
    // PlanDefinition, then fails its whole commit on a 403.
    if (routes.length === 0) return { id: node.id, definitionCanonical: node.questionnaire };
    return {
      id: node.id,
      definitionCanonical: node.questionnaire,
      action: routes.map((edge) => ({
        id: edge.id,
        extension: factConditionExtensions(edge.conditions).length
          ? factConditionExtensions(edge.conditions)
          : undefined,
        relatedAction: [{ actionId: edge.to, relationship: "before-start" as const }],
        ...(edge.conditions.length === 0
          ? {}
          : {
              condition: [
                {
                  kind: "applicability" as const,
                  expression: {
                    language: FLOW_CONDITION_LANGUAGE,
                    expression: JSON.stringify(
                      omitUndefined({
                        conditions: edge.conditions.filter((condition) => !("fact" in condition)),
                        conditionBehavior: edge.conditionBehavior,
                      }),
                    ),
                  },
                },
              ],
            }),
      })),
    };
  });
}

function extensionsToFhir(props: Props, prior: PlanDefinition | null): Extension[] {
  return [
    ...(prior?.extension ?? []).filter((extension) => !OWNED_EXTENSION_URLS.has(extension.url)),
    ...(props.nodeHighWaterMark === undefined
      ? []
      : [{ url: FLOW_NODE_SEQUENCE_URL, valueInteger: props.nodeHighWaterMark }]),
    ...(props.edgeHighWaterMark === undefined
      ? []
      : [{ url: FLOW_EDGE_SEQUENCE_URL, valueInteger: props.edgeHighWaterMark }]),
  ];
}

function decode(resource: PlanDefinition): Props {
  return omitUndefined({
    spine: {
      url: required(resource, "url", resource.url),
      version: Number(required(resource, "version", resource.version)),
      mintedAt: required(resource, "mintedAt", resource.date),
      name: required(resource, "name", resource.title),
      description: resource.description,
      availability: resource.status === "active" ? ("Active" as const) : ("Inactive" as const),
    },
    ...graphFromActions(resource.action),
    nodeHighWaterMark: extensionOf(resource, FLOW_NODE_SEQUENCE_URL)?.valueInteger,
    edgeHighWaterMark: extensionOf(resource, FLOW_EDGE_SEQUENCE_URL)?.valueInteger,
  });
}

function encode(props: Props, prior: PlanDefinition | null): PlanDefinition {
  return omitUndefined({
    resourceType: "PlanDefinition" as const,
    meta: {
      ...prior?.meta,
      tag: [
        ...(prior?.meta?.tag ?? []).filter(
          (tag) => !(tag.system === CATALOG_MODEL_KIND_SYSTEM && tag.code === FLOW_DEFINITION_KIND),
        ),
        catalogModelKindTag(FLOW_DEFINITION_KIND),
      ],
    },
    url: props.spine.url,
    version: String(props.spine.version),
    date: props.spine.mintedAt,
    ...catalogNameToFhir(props.spine.name),
    description: props.spine.description,
    status: CATALOG_AVAILABILITY[props.spine.availability],
    type: planDefinitionKind(PLAN_DEFINITION_KIND.flow),
    action: actionsFromGraph(props),
    extension: extensionsToFhir(props, prior),
  });
}

export const flowDefinitionCodec: Codec<Props, keyof Props, PlanDefinition> = {
  resourceType: "PlanDefinition",
  reads: {
    spine: [fp("url"), fp("version"), fp("date"), fp("title"), fp("description"), fp("status")],
    nodes: [fp("action")],
    edges: [fp("action")],
    nodeHighWaterMark: [fp(`extension.where(url = '${FLOW_NODE_SEQUENCE_URL}').valueInteger`)],
    edgeHighWaterMark: [fp(`extension.where(url = '${FLOW_EDGE_SEQUENCE_URL}').valueInteger`)],
  },
  owns: [fp("action"), fp("extension")],
  decode,
  encode,
};
